"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const countrySelectorInputs = [
  {
    name: "list",
    type: "list",
    required: true,
    helperText: "Array of available countries",
    subFields: [
      {
        name: "countryCode",
        type: "string",
        required: true,
        helperText: "ISO code for the country",
      },
      {
        name: "countryName",
        type: "string",
        required: true,
        helperText: "Display name of the country",
      },
      {
        name: "fileReference",
        type: "file",
        required: true,
        helperText: "Reference to the country data file",
      },
      {
        name: "url",
        type: "string",
        required: true,
        helperText: "URL to the country-specific page",
      },
    ],
  },
  {
    name: "links",
    type: "list",
    helperText: "Optional manual links to display",
    subFields: [
      {
        name: "path",
        type: "string",
        required: true,
        helperText: "The link path",
      },
      {
        name: "fileReference",
        type: "file",
        required: true,
        allowedFileTypes: IMAGE_FILE_TYPES,
        helperText: "Reference to the manual link file",
      },
      {
        name: "displayLink",
        type: "string",
        required: true,
        helperText: "Text to display for the link",
      },
    ],
  },
  {
    name: "text",
    type: "string",
    helperText: "Optional descriptive text",
  },
  {
    name: "countriesApi",
    type: "string",
    required: true,
    helperText: "URL for the countries API endpoint",
  },
  {
    name: "logoFileReference",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "backgroundImage",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "healthBanner",
    type: "uiBlocks",
    defaultValue: [
      {
        "@type": "@builder.io/sdk:Element",
        component: {
          name: "HealthBanner",
          options: {
            content: "18+. THESE PRODUCTS ARE NOT RISK‑FREE AND ARE ADDICTIVE",
            variant: "default",
          },
        },
      },
    ],
    onChange: (options: Map<string, unknown>) => {
      const items = options.get("healthBanner");

      if (!Array.isArray(items)) {
        return;
      }

      const onlyHealthBanner = items.filter(
        (item) => item.component?.name === "HealthBanner"
      );

      if (onlyHealthBanner.length > 1) {
        options.set("healthBanner", onlyHealthBanner.slice(0, 1));
        alert("You can only add one HealthBanner component here.");
        return;
      }

      if (onlyHealthBanner.length < items.length) {
        options.set("healthBanner", onlyHealthBanner);
        alert("Only HealthBanner components are allowed in this slot.");
      }
    },
  },
  {
    name: "richText",
    type: "uiBlocks",
    defaultValue: [
      {
        "@type": "@builder.io/sdk:Element",
        component: {
          name: "RichText",
          options: {
            text: "Text here...",
          },
        },
      },
    ],
    onChange: (options: Map<string, unknown>) => {
      const items = options.get("richText");

      if (!Array.isArray(items)) {
        return;
      }

      const onlyRichText = items.filter(
        (item) => item.component?.name === "RichText"
      );

      if (onlyRichText.length > 1) {
        options.set("richText", onlyRichText.slice(0, 1));
        alert("You can only add one RichText component here.");
        return;
      }

      if (onlyRichText.length < items.length) {
        options.set("richText", onlyRichText);
        alert("Only RichText components are allowed in this slot.");
      }
    },
  },
] as const satisfies Inputs;

export const CountrySelectorComponent: RegisteredComponent = {
  name: "CountrySelector",
  component: dynamic(async () =>
    withValidation(
      countrySelectorInputs,
      (await import("./CountrySelector")).CountrySelector,
      "CountrySelector"
    )
  ),
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  canHaveChildren: true,
  inputs: countrySelectorInputs,
};
